/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import Login from './components/Login';
import CreateAccount from './components/CreateAccount';
import AddCar from './components/AddCar';
import Inventory from './components/Inventory';
import Home from './components/Home';
import AddExpenseReport from './components/AddExpenseReport';
import EnterDetailsManually from './components/EnterDetailsManually';
import ViewDeal from './components/ViewDeal';
import PaymentManagement from './components/PaymentManagement';
import SplashScreen from './components/SplashScreen';
import UserSettings from './components/UserSettings'; // Import the UserSettings component
import { AppBar, Toolbar, Typography, Box, Drawer, List, ListItem, ListItemText, IconButton, CssBaseline, useMediaQuery, Button } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import EditCar from './components/EditCar'; // Import your EditCar component
import EditReport from './components/EditReport';  // Assuming EditReport is the component for editing the report
import { ThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from './theme';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import { motion, AnimatePresence } from 'framer-motion';
import HomeIcon from '@mui/icons-material/Home';
import InventoryIcon from '@mui/icons-material/Inventory';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import LoginIcon from '@mui/icons-material/Login';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import StorefrontIcon from '@mui/icons-material/Storefront';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MonthlyReconditioning from './components/MonthlyReconditioning';
import UnsoldReconditioning from './components/UnsoldReconditioning';
import MonthlyProfits from './components/MonthlyProfits';
import AssignmentIcon from '@mui/icons-material/Assignment';
import Tasks from './components/Tasks';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Expenses from './components/Expenses';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import Deposits from './components/Deposits';
import RegistrationSuccess from './components/RegistrationSuccess';
import RegistrationCancelled from './components/RegistrationCancelled';
import useIsMobile from './hooks/useIsMobile';
import PaymentSuccess from './components/PaymentSuccess'; // Import the new component
import InventoryManagementPage from './components/InventoryManagementPage';
import CostTrackingPage from './components/CostTrackingPage';
import PerformanceMetricsPage from './components/PerformanceMetricsPage';
import DealAnalysisPage from './components/DealAnalysisPage';
import RealTimeAnalyticsPage from './components/RealTimeAnalyticsPage';
import DocumentManagementPage from './components/DocumentManagementPage';
import ProfitTrackingPage from './components/ProfitTrackingPage';
import TaskManagementPage from './components/TaskManagementPage'; // Import Task Management Page
import FinancialManagementPage from './components/FinancialManagementPage'; // Import Financial Management Page
import Payments from './components/Payments';
import PaymentsDashboard from './components/PaymentsDashboard';
import PaymentIcon from '@mui/icons-material/Payment';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [companyName, setCompanyName] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode ? JSON.parse(savedMode) : false;
  });
  const isMobile = useIsMobile();
  const drawerWidth = 90;

  // Determine if the main layout should be hidden (Splash or Feature pages)
  const hideMainLayout = location.pathname === '/' || location.pathname.startsWith('/features/');

  // Fetch token and company name from localStorage on component mount
  useEffect(() => {
    const username = localStorage.getItem('username');
    const storedCompanyName = localStorage.getItem('company_name');
    
    if (storedCompanyName) {
      setCompanyName(storedCompanyName);
    }
    if (username) {
      setIsAuthenticated(true);
    }
  }, []);

  const handleLogin = (user_id) => {
    const username = localStorage.getItem('username');
    const company_name = localStorage.getItem('company_name');
    
    if (!username || !company_name) {
      console.error('Missing user data in localStorage');
      return;
    }
    
    setCompanyName(company_name);
    setIsAuthenticated(true);
    navigate('/home');
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('username');
    localStorage.removeItem('company_name');
    setIsAuthenticated(false);
    setCompanyName('');
    navigate('/login');
  };

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const toggleTheme = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('darkMode', JSON.stringify(newMode));
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setDrawerOpen(false);
    }
  };

  const menuItemStyle = {
    my: 0.5,
    color: 'white',
    borderLeft: '4px solid transparent',
    '&.Mui-selected': {
      backgroundColor: 'transparent',
      borderLeft: '4px solid #4285f4',
      color: '#4285f4',
      '& .MuiListItemIcon-root': {
        color: '#4285f4',
      },
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.08)',
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    }
  };

  console.log('API URL:', process.env.REACT_APP_API_URL);

  return (
    <ThemeProvider theme={isDarkMode ? darkTheme : lightTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <CssBaseline />

        {/* Only render AppBar and Drawer if not on splash screen OR feature page */}
        {!hideMainLayout && (
          <>
            {/* AppBar */}
            <AppBar
              position="fixed"
              sx={{
                width: '100%',
                background: '#4285f4',
                height: '60px',
                zIndex: (theme) => theme.zIndex.drawer + 1,
              }}
            >
              <Toolbar sx={{ 
                minHeight: '60px !important',
                height: '60px',
                px: { xs: 1, sm: 2 }
              }}>
                {isMobile && (
                  <IconButton
                    color="inherit"
                    aria-label="open drawer"
                    edge="start"
                    onClick={toggleDrawer}
                    sx={{ mr: 2 }}
                  >
                    <MenuIcon />
                  </IconButton>
                )}
                <Typography 
                  variant="h6" 
                  component="div" 
                  sx={{ 
                    flexGrow: 1,
                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                    fontWeight: 600,
                    color: 'white',
                  }}
                >
                  {("DealCost, " + companyName)}
                </Typography>
                {!isMobile && (
                  <Button 
                    color="inherit" 
                    onClick={() => handleNavigation('/home')}
                    startIcon={<HomeIcon />}
                  >
                    Home
                  </Button>
                )}
                <IconButton 
                  sx={{ ml: 1 }} 
                  onClick={toggleTheme} 
                  color="inherit"
                >
                  {isDarkMode ? <Brightness7Icon /> : <Brightness4Icon />}
                </IconButton>
              </Toolbar>
            </AppBar>

            {/* Sidebar for desktop */}
            {!isMobile && (
              <Drawer
                variant="permanent"
                sx={{
                  width: drawerWidth,
                  flexShrink: 0,
                  [`& .MuiDrawer-paper`]: { 
                    width: drawerWidth, 
                    boxSizing: 'border-box',
                    backgroundColor: '#2c3e50',
                    color: 'white',
                    borderRight: 'none',
                    pt: '60px', // Add padding top to account for AppBar
                  },
                }}
              >
                <List sx={{ pt: 0 }}>
                  {isAuthenticated ? (
                    <>
                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/home')}
                        selected={location.pathname === '/home'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <HomeIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Home
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/add-car')}
                        selected={location.pathname === '/add-car'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <DirectionsCarIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                          Add Vehicle
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/inventory')}
                        selected={location.pathname === '/inventory'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <InventoryIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Inventory
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/tasks')}
                        selected={location.pathname === '/tasks'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AssignmentIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          To-Do List
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/expenses')}
                        selected={location.pathname === '/expenses'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountBalanceWalletIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Expenses
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/deposits')}
                        selected={location.pathname === '/deposits'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountBalanceIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Deposits
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/payments-dashboard')}
                        selected={location.pathname === '/payments-dashboard'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <PaymentIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Payments
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/user-settings')}
                        selected={location.pathname === '/user-settings'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountCircleIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                          My Account
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={handleLogout}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <LogoutIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Logout
                        </Typography>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/')}
                        selected={location.pathname === '/'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AttachMoneyIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          DealCost
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/login')}
                        selected={location.pathname === '/login'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <LoginIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Login
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/create-account')}
                        selected={location.pathname === '/create-account'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <PersonAddIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Create
                        </Typography>
                      </ListItem>
                    </>
                  )}
                </List>
              </Drawer>
            )}

            {/* Sidebar for mobile */}
            {isMobile && (
              <Drawer
                variant="temporary"
                open={drawerOpen}
                onClose={toggleDrawer}
                ModalProps={{
                  keepMounted: true, // Better open performance on mobile
                }}
                sx={{
                  display: { xs: 'block', sm: 'none' },
                  '& .MuiDrawer-paper': { 
                    width: drawerWidth,
                    backgroundColor: '#2c3e50',
                    color: 'white',
                    pt: '60px', // Add padding top to account for AppBar
                  },
                }}
              >
                <List>
                  {isAuthenticated ? (
                    <>
                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/home')}
                        selected={location.pathname === '/home'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <HomeIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Home
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/add-car')}
                        selected={location.pathname === '/add-car'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <DirectionsCarIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                          Add Vehicle
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/inventory')}
                        selected={location.pathname === '/inventory'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <InventoryIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Inventory
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/tasks')}
                        selected={location.pathname === '/tasks'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AssignmentIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          To-Do List
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/expenses')}
                        selected={location.pathname === '/expenses'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountBalanceWalletIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Expenses
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/deposits')}
                        selected={location.pathname === '/deposits'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountBalanceIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Deposits
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/payments-dashboard')}
                        selected={location.pathname === '/payments-dashboard'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <PaymentIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Payments
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/user-settings')}
                        selected={location.pathname === '/user-settings'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AccountCircleIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem', textAlign: 'center' }}>
                          My Account
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={handleLogout}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <LogoutIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Logout
                        </Typography>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/')}
                        selected={location.pathname === '/'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <AttachMoneyIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          DealCost
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/login')}
                        selected={location.pathname === '/login'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <LoginIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Login
                        </Typography>
                      </ListItem>

                      <ListItem 
                        button 
                        onClick={() => handleNavigation('/create-account')}
                        selected={location.pathname === '/create-account'}
                        sx={{
                          ...menuItemStyle,
                          flexDirection: 'column',
                          height: 80,
                          justifyContent: 'center',
                          p: 0,
                          m: 0
                        }}
                      >
                        <PersonAddIcon />
                        <Typography variant="caption" sx={{ mt: 0.5, fontSize: '0.7rem' }}>
                          Create
                        </Typography>
                      </ListItem>
                    </>
                  )}
                </List>
              </Drawer>
            )}
          </>
        )}

        {/* Main content */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            ...(hideMainLayout
              ? { p: 0, m: 0, width: '100%' } // Full width, no padding/margin if layout hidden
              : {
                  p: { xs: 1, sm: 2, md: 3 },
                  mt: '60px', // Margin top for AppBar
                  ml: isMobile ? 0 : `${drawerWidth}px`, // Margin left for Drawer
                  width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)` // Adjust width for Drawer
                })
          }}
        >
          <Routes>
            <Route path="/" element={<SplashScreen />} />
            <Route path="/login" element={isAuthenticated ? <Navigate to="/home" /> : <Login onLogin={handleLogin} />} />
            <Route path="/create-account" element={<CreateAccount />} />
            <Route path="/registration-success" element={<RegistrationSuccess />} />
            <Route path="/registration-cancelled" element={<RegistrationCancelled />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />

            {/* Feature Pages */}
            <Route path="/features/inventory-management" element={<InventoryManagementPage />} />
            <Route path="/features/cost-tracking" element={<CostTrackingPage />} />
            <Route path="/features/performance-metrics" element={<PerformanceMetricsPage />} />
            <Route path="/features/deal-analysis" element={<DealAnalysisPage />} />
            <Route path="/features/real-time-analytics" element={<RealTimeAnalyticsPage />} />
            <Route path="/features/document-management" element={<DocumentManagementPage />} />
            <Route path="/features/profit-tracking" element={<ProfitTrackingPage />} />
            <Route path="/features/task-management" element={<TaskManagementPage />} />
            <Route path="/features/financial-management" element={<FinancialManagementPage />} />

            {isAuthenticated ? (
              <>
                <Route path="/home" element={<Home />} />
                <Route path="/add-car" element={<AddCar />} />
                <Route path="/inventory" element={<Inventory />} />
                <Route path="/add-expense-report" element={<AddExpenseReport />} />
                <Route path="/enter-details-manually" element={<EnterDetailsManually />} />
                <Route path="/view-deal/:vin" element={<ViewDeal />} />
                <Route path="/payments-dashboard" element={<PaymentsDashboard />} />
                <Route path="/payments/:vin" element={<Payments />} />
                <Route path="/user-settings" element={<UserSettings />} />
                <Route path="/edit-car/:vin" element={<EditCar />} />
                <Route path="/edit-report/:reportId" element={<EditReport />} />
                <Route path="/monthly-reconditioning" element={<MonthlyReconditioning />} />
                <Route path="/unsold-reconditioning" element={<UnsoldReconditioning />} />
                <Route path="/monthly-profits" element={<MonthlyProfits />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/deposits" element={<Deposits />} />
              </>
            ) : (
              <Route path="*" element={<Navigate to="/login" />} />
            )}
          </Routes>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;